"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useEffect, useMemo } from "react";

export const BreadcrumbHomepage = () => {
  const pathname = usePathname();
  const t = useTranslations("navigation");

  // Extraire la locale et le path
  const pathSegments = pathname.split("/").filter(Boolean);
  const locale = pathSegments[0] || "fr";
  const currentPath = pathSegments[1] || "";

  // Mapping des paths vers les labels
  const pathLabels = useMemo(
    (): { [key: string]: string } => ({
      cookies: t("cookies") || "Cookies",
      "mentions-legales": t("legalNotice") || "Mentions légales",
    }),
    [t],
  );

  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        name: t("home") || "Accueil",
        href: `/${locale}/`,
        current: false,
      },
    ];

    if (currentPath) {
      items.push({
        name: pathLabels[currentPath] || currentPath,
        href: pathname,
        current: true,
      });
    }

    return items;
  }, [t, locale, currentPath, pathLabels, pathname]);

  // Schéma JSON-LD pour les breadcrumbs
  const breadcrumbSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: `https://www.virtuosa.fr${item.href}`,
      })),
    }),
    [breadcrumbItems],
  );

  useEffect(() => {
    if (currentPath) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(breadcrumbSchema);
      script.id = "breadcrumb-schema";

      // Supprimer l'ancien schéma s'il existe
      const existingScript = document.getElementById("breadcrumb-schema");
      if (existingScript) {
        existingScript.remove();
      }

      document.head.appendChild(script);

      return () => {
        const scriptToRemove = document.getElementById("breadcrumb-schema");
        if (scriptToRemove) {
          scriptToRemove.remove();
        }
      };
    }
  }, [pathname, breadcrumbSchema, currentPath]);

  if (breadcrumbItems.length <= 1) {
    return null;
  }

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-8"
      itemScope
      itemType="https://schema.org/BreadcrumbList"
    >
      <ol className="flex items-center space-x-2 text-sm text-accent">
        {breadcrumbItems.map((item, index) => (
          <motion.li
            key={item.href}
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: index * 0.1 }}
            className="flex items-center"
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            {index > 0 && (
              <Icon
                icon="heroicons:chevron-right"
                className="mx-2 h-4 w-4 text-accent"
              />
            )}
            {item.current ? (
              <span
                className="font-medium text-secondary"
                itemProp="name"
                aria-current="page"
              >
                {item.name}
              </span>
            ) : (
              <Link
                href={item.href}
                className="text-accent transition-colors hover:text-secondary"
                itemProp="item"
              >
                <span itemProp="name">{item.name}</span>
              </Link>
            )}
            <meta itemProp="position" content={String(index + 1)} />
          </motion.li>
        ))}
      </ol>
    </nav>
  );
};
